import { ref } from 'vue'
import type { CSSProperties, Ref } from 'vue'
import { useEventListener, useResizeObserver } from '@vueuse/core'

/**
 * One highlight that glides from item to item (mouse or keyboard) instead of each item lighting up on its own.
 * Items are the `selector` elements inside `container`, which must be `position: relative`; each sits in an `li` row.
 * An item's `data-tone` (a colour token such as `primary-500`) colours the highlight.
 */
export function useMagneticHighlight(container: Ref<HTMLElement | null>, selector: string) {
  const style = ref<CSSProperties>({ opacity: 0 })
  let current: HTMLElement | null = null

  const place = (item: HTMLElement, glide: boolean) => {
    const box = container.value?.getBoundingClientRect()
    if (!box) return
    const rect = item.getBoundingClientRect()
    style.value = {
      transform: `translate3d(${rect.left - box.left}px, ${rect.top - box.top}px, 0)`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      '--tone': `var(--color-${item.dataset.tone || 'primary-500'})`,
      opacity: 1,
      // Shows up in place rather than gliding in from wherever it last was.
      ...(glide ? {} : { transitionProperty: 'opacity' }),
    }
    current = item
  }

  const hide = () => {
    current = null
    style.value = { ...style.value, opacity: 0, transitionProperty: 'opacity' }
  }

  // Anywhere on an item's row counts (e.g. the tree lines left of a sub-page).
  const itemOf = (target: EventTarget | null) => {
    const element = target instanceof Element ? target : null
    const item = element?.closest(selector) ?? element?.closest('li')?.querySelector(selector)
    return item instanceof HTMLElement && container.value?.contains(item) ? item : null
  }

  // Between two items (a separator, a gap) it stays on the last one: that is the magnet.
  useEventListener(container, 'pointerover', (event: PointerEvent) => {
    const item = itemOf(event.target)
    if (item && item !== current) place(item, !!current)
  })
  useEventListener(container, 'pointerleave', hide)

  useEventListener(container, 'focusin', (event: FocusEvent) => {
    const item = itemOf(event.target)
    if (item && item.matches(':focus-visible')) place(item, !!current)
  })
  useEventListener(container, 'focusout', (event: FocusEvent) => {
    if (!container.value?.contains(event.relatedTarget as Node | null)) hide()
  })

  // The drawer widening (or a sub-menu folding) moves the items: follow without gliding.
  useResizeObserver(container, () => {
    if (current?.isConnected) place(current, false)
    else if (current) hide()
  })

  return { style }
}
